<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b">
      <div class="container mx-auto px-4 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <button @click="goBack" class="text-gray-600 hover:text-gray-800 mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <i class="fas fa-arrow-left"></i>
            </button>
            <h1 class="text-2xl font-bold text-gray-800">
              {{ appStore.currentApp?.name || 'Kitaplık Yönetimi' }}
            </h1>
            <span class="ml-4 text-sm text-gray-600">Dosya Yöneticisi</span>
          </div>
          
          <div class="flex items-center gap-3">
            <!-- Header Three Dots Menu -->
            <div class="relative">
              <button
                @click="toggleHeaderDropdown"
                class="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
              >
                <i class="fas fa-ellipsis-vertical"></i>
              </button>
              
              <!-- Header Dropdown Menu -->
              <div
                v-if="showHeaderDropdown"
                class="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20"
                @click.stop
              >
                <div class="py-1">
                  <button
                    @click="togglePublish"
                    :disabled="appStore.loading"
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                    :class="appStore.currentApp?.isPublished ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'"
                  >
                    <i :class="appStore.currentApp?.isPublished ? 'fas fa-eye-slash' : 'fas fa-globe'" class="mr-3 w-4"></i>
                    {{ appStore.currentApp?.isPublished ? 'Yayından Kaldır' : 'Yayınla' }}
                  </button>
                  
                  <button
                    v-if="appStore.currentApp?.isPublished"
                    @click="copyPublicLink"
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                  >
                    <i class="fas fa-link mr-3 w-4"></i>
                    Link Kopyala
                  </button>
                  
                  <button
                    v-if="appStore.currentApp?.isPublished"
                    @click="showQRCode"
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                  >
                    <i class="fas fa-qrcode mr-3 w-4"></i>
                    QR Kod Göster
                  </button>
                  
                  <hr class="my-1">
                  
                  <button
                    @click="deleteApp"
                    :disabled="appStore.loading"
                    class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center transition-colors"
                  >
                    <i class="fas fa-trash mr-3 w-4"></i>
                    Sil
                  </button>
                  
                  <hr class="my-1">
                  
                  <button
                    @click="goBack"
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                  >
                    <i class="fas fa-home mr-3 w-4"></i>
                    Ana Sayfaya Dön
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <div class="container mx-auto px-4 py-8">
      <!-- Files and Folders Section -->
      <div class="bg-white rounded-xl shadow-lg border border-gray-100 relative">

        <!-- Section Header -->
        <div class="p-8 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-6">
              <div class="flex items-center text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                <i class="fas fa-folder mr-2 text-blue-500"></i>
                {{ currentSubfolders.length }} klasör
              </div>
              <div class="flex items-center text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                <i class="fas fa-file mr-2 text-green-500"></i>
                {{ currentFiles.length }} dosya
              </div>
            </div>
            
            <div class="flex items-center gap-3">
              <button
                @click="showUpload = true"
                class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                <i class="fas fa-cloud-upload-alt mr-2"></i>
                Dosya Yükle
              </button>
              
              <button
                @click="showCreateFolder = true"
                class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <i class="fas fa-folder-plus mr-2"></i>
                Yeni Klasör
              </button>
            </div>
          </div>
          
          <!-- Inline Upload Section -->
          <div v-if="showUpload" class="mt-6 p-6 bg-gray-50 rounded-xl border border-gray-200">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-semibold text-gray-800">
                <i class="fas fa-cloud-upload-alt text-green-600 mr-2"></i>
                Dosya Yükle
              </h3>
              <button
                @click="showUpload = false"
                class="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            <ChunkedUploader 
              ref="chunkedUploader"
              :app-id="appId"
              :folder-id="fileStore.currentFolderId"
              @error="handleUploadError"
            />
            <div v-if="uploadError" class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div class="flex items-center">
                <i class="fas fa-exclamation-circle text-red-500 mr-2"></i>
                <p class="text-red-600 text-sm">{{ uploadError }}</p>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Files and Folders List -->
        <div class="p-8">
          <!-- Breadcrumb Navigation -->
          <nav v-if="folderBreadcrumbs.length > 0" class="mb-8">
            <div class="flex items-center space-x-2 text-sm bg-gray-50 px-4 py-3 rounded-lg">
              <i class="fas fa-home text-gray-400"></i>
              <button @click="navigateToFolder(null)" class="text-blue-600 hover:text-blue-800 font-medium">
                Ana Klasör
              </button>
              <span v-for="folder in folderBreadcrumbs" :key="folder.id" class="flex items-center">
                <i class="fas fa-chevron-right text-gray-400 mx-2"></i>
                <button 
                  @click="navigateToFolder(folder.id)"
                  class="text-blue-600 hover:text-blue-800 font-medium"
                >
                  {{ folder.name }}
                </button>
              </span>
            </div>
          </nav>

          <!-- Folders -->
          <div v-if="currentSubfolders.length > 0" class="mb-8">
            <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <i class="fas fa-folder text-blue-500 mr-2"></i>
              Klasörler
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <div
                v-for="folder in currentSubfolders"
                :key="folder.id"
                @click="navigateToFolder(folder.id)"
                class="bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-md cursor-pointer group transition-all duration-200 relative"
              >
                <div class="flex flex-col items-center text-center">
                  <div class="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
                    <i class="fas fa-folder text-blue-500 text-2xl"></i>
                  </div>
                  <h4 class="font-medium text-gray-800 text-sm truncate w-full mb-2">{{ folder.name }}</h4>
                  
                  <!-- Folder Actions -->
                  <div class="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div class="relative">
                      <button
                        @click.stop="toggleFolderDropdown(folder.id)"
                        class="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <i class="fas fa-ellipsis-vertical text-sm"></i>
                      </button>
                      
                      <!-- Folder Dropdown Menu -->
                      <div
                        v-if="activeFolderDropdown === folder.id"
                        class="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-10"
                        @click.stop
                      >
                        <div class="py-1">
                          <button
                            @click="editFolder(folder)"
                            class="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                          >
                            <i class="fas fa-edit mr-2 w-3"></i>
                            Yeniden Adlandır
                          </button>
                          <button
                            @click="deleteFolder(folder)"
                            class="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 flex items-center transition-colors"
                          >
                            <i class="fas fa-trash mr-2 w-3"></i>
                            Sil
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Files -->
          <div v-if="currentFiles.length > 0">
            <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <i class="fas fa-file text-green-500 mr-2"></i>
              Dosyalar
            </h3>
            <div class="space-y-2">
              <div
                v-for="file in currentFiles"
                :key="file.id"
                class="bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-md group transition-all duration-200"
                :class="{ 'border-blue-200 bg-blue-50': getFileDisplayInfo(file).isUploading }"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center flex-1 min-w-0">
                    <div class="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 relative">
                      <i :class="getFileIcon(file)" class="text-lg"></i>
                      <!-- Upload Status Indicator -->
                      <div 
                        v-if="getFileDisplayInfo(file).isUploading"
                        class="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center"
                      >
                        <i 
                          v-if="getFileDisplayInfo(file).status === 'uploading'"
                          class="fas fa-spinner fa-spin text-blue-500 text-xs"
                        ></i>
                        <i 
                          v-else-if="getFileDisplayInfo(file).status === 'processing'"
                          class="fas fa-cog fa-spin text-yellow-500 text-xs"
                        ></i>
                        <i 
                          v-else-if="getFileDisplayInfo(file).status === 'error'"
                          class="fas fa-exclamation-triangle text-red-500 text-xs"
                        ></i>
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="font-medium text-gray-800 truncate text-sm">{{ file.name }}</div>
                      <div class="text-xs text-gray-500 mt-1">
                        {{ getFileDisplayInfo(file).size }} • {{ getFileDisplayInfo(file).date }}
                      </div>
                      <!-- Upload Progress Bar -->
                      <div 
                        v-if="getFileDisplayInfo(file).isUploading && getFileDisplayInfo(file).status !== 'error'"
                        class="w-full bg-gray-200 rounded-full h-1.5 mt-2"
                      >
                        <div 
                          class="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                          :style="{ width: getFileDisplayInfo(file).progress + '%' }"
                        ></div>
                      </div>
                      <!-- Error Message -->
                      <div 
                        v-if="getFileDisplayInfo(file).status === 'error'"
                        class="text-xs text-red-600 mt-1"
                      >
                        {{ getFileDisplayInfo(file).error || 'Yükleme hatası' }}
                      </div>
                    </div>
                  </div>
                  
                  <!-- File Actions -->
                  <div 
                    v-if="!getFileDisplayInfo(file).isUploading"
                    class="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <a
                      :href="(file as FileItem).s3Url"
                      target="_blank"
                      class="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Dosyayı İndir"
                    >
                      <i class="fas fa-download text-sm"></i>
                    </a>
                    <button
                      @click="editFile(file as FileItem)"
                      class="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Yeniden Adlandır"
                    >
                      <i class="fas fa-edit text-sm"></i>
                    </button>
                    <button
                      @click="deleteFile(file as FileItem)"
                      class="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Dosyayı Sil"
                    >
                      <i class="fas fa-trash text-sm"></i>
                    </button>
                  </div>
                  
                  <!-- Upload Actions -->
                  <div 
                    v-if="getFileDisplayInfo(file).isUploading"
                    class="flex items-center gap-2"
                  >
                    <button
                      v-if="getFileDisplayInfo(file).status === 'error'"
                      @click="fileStore.removeUploadingFile((file as UploadingFile).uploadId)"
                      class="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Hatayı Temizle"
                    >
                      <i class="fas fa-times text-sm"></i>
                    </button>
                    <div 
                      v-else
                      class="text-xs text-blue-600 font-medium px-2 py-1 bg-blue-100 rounded"
                    >
                      %{{ getFileDisplayInfo(file).progress }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="!fileStore.loading && currentSubfolders.length === 0 && currentFiles.length === 0" class="text-center py-16">
            <div class="text-gray-400 mb-6">
              <i class="fas fa-folder-open text-6xl"></i>
            </div>
            <h3 class="text-xl font-medium text-gray-600 mb-2">Bu klasör boş</h3>
            <p class="text-gray-500 mb-6">Başlamak için dosya yükleyin veya klasör oluşturun.</p>
            <div class="flex justify-center gap-4">
              <button
                @click="showCreateFolder = true"
                class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <i class="fas fa-folder-plus mr-2"></i>
                Yeni Klasör
              </button>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="fileStore.loading" class="text-center py-16">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p class="text-gray-600 mt-4">Dosyalar yükleniyor...</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Folder Modal -->
    <div v-if="showCreateFolder" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-xl p-8 w-full max-w-md">
        <h3 class="text-xl font-semibold text-gray-800 mb-6">Yeni Klasör Oluştur</h3>
        <form @submit.prevent="handleCreateFolder">
          <input
            v-model="newFolderName"
            type="text"
            placeholder="Klasör adı..."
            required
            class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-6"
          >
          <div class="flex gap-3">
            <button
              type="button"
              @click="cancelCreateFolder"
              class="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
            >
              İptal
            </button>
            <button
              type="submit"
              :disabled="fileStore.loading"
              class="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
            >
              Oluştur
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Rename Modal -->
    <div v-if="itemToRename" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-xl p-8 w-full max-w-md">
        <h3 class="text-xl font-semibold text-gray-800 mb-6">
          {{ itemToRename.type === 'folder' ? 'Klasörü' : 'Dosyayı' }} Yeniden Adlandır
        </h3>
        <form @submit.prevent="handleRename">
          <input
            v-model="renameValue"
            type="text"
            required
            class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-6"
          >
          <div class="flex gap-3">
            <button
              type="button"
              @click="cancelRename"
              class="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
            >
              İptal
            </button>
            <button
              type="submit"
              :disabled="fileStore.loading"
              class="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
            >
              Yeniden Adlandır
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- QR Code Modal -->
    <div
      v-if="showQRModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click="showQRModal = false"
    >
      <div
        class="bg-white rounded-xl p-6 max-w-sm w-full"
        @click.stop
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-800">QR Kod</h3>
          <button
            @click="showQRModal = false"
            class="text-gray-400 hover:text-gray-600 text-xl transition-colors"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- QR Code Display -->
        <div class="text-center mb-4">
          <div class="inline-block p-4 bg-white rounded-lg shadow-sm border">
            <QRCodeGenerator v-if="appStore.currentApp" :app="appStore.currentApp" :show-directly="true" />
          </div>
        </div>

        <!-- App Name -->
        <div class="text-center mb-4">
          <p class="text-sm text-gray-600">{{ appStore.currentApp?.name }}</p>
        </div>

        <!-- Quick Instructions -->
        <div class="bg-blue-50 rounded-lg p-3">
          <p class="text-xs text-blue-700 text-center">
            <i class="fas fa-mobile-alt mr-1"></i>
            Mobil uygulamanızdan QR kodu tarayın
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAppStore } from '../stores/appStore'
import { useFileStore, type UploadingFile } from '../stores/fileStore'
import ChunkedUploader from '../components/ChunkedUploader.vue'
import QRCodeGenerator from '../components/QRCodeGenerator.vue'
import type { FileItem, Folder } from '../types/api'

const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
const fileStore = useFileStore()

const appId = route.params.appId as string

const showCreateFolder = ref<boolean>(false)
const newFolderName = ref<string>('')
const itemToRename = ref<(FileItem | Folder) & { type: 'file' | 'folder' } | null>(null)
const renameValue = ref<string>('')
const uploadError = ref<string | null>(null)
const showHeaderDropdown = ref<boolean>(false)
const activeFolderDropdown = ref<string | null>(null)
const showUpload = ref<boolean>(false)
const showQRModal = ref<boolean>(false)

// Set document title
document.title = 'AP Kütüphane - Kitaplık Yönetimi'

// Safely get the base URL
const baseUrl = computed((): string => {
  if (typeof window !== 'undefined') {
    return window.location.origin
  }
  return ''
})

// Computed properties for current folder content
const currentSubfolders = computed(() => {
  return fileStore.getSubfoldersInCurrentFolder
})

const currentFiles = computed(() => {
  return fileStore.getFilesInCurrentFolder
})

// Build breadcrumb navigation based on current folder
const folderBreadcrumbs = computed(() => {
  const breadcrumbs: Folder[] = []
  let currentFolderId = fileStore.currentFolderId
  
  while (currentFolderId) {
    const folder = fileStore.getFolderById(currentFolderId)
    if (folder) {
      breadcrumbs.unshift(folder)
      currentFolderId = folder.parentId || null
    } else {
      break
    }
  }
  
  return breadcrumbs
})

const goBack = (): void => {
  router.push('/dashboard')
}

const toggleHeaderDropdown = (): void => {
  showHeaderDropdown.value = !showHeaderDropdown.value
}

const togglePublish = async (): Promise<void> => {
  try {
    if (appStore.currentApp) {
      await appStore.updateApp(appStore.currentApp.id, { isPublished: !appStore.currentApp.isPublished })
      showHeaderDropdown.value = false
    }
  } catch (error) {
    console.error('Failed to toggle publish:', error)
  }
}

const copyPublicLink = async (): Promise<void> => {
  try {
    if (appStore.currentApp) {
      const url = `${baseUrl.value}/app/${appStore.currentApp.id}`
      await navigator.clipboard.writeText(url)
      showHeaderDropdown.value = false
    }
  } catch (error) {
    console.error('Failed to copy link:', error)
  }
}

const showQRCode = (): void => {
  showQRModal.value = true
  showHeaderDropdown.value = false
}

const deleteApp = async (): Promise<void> => {
  if (appStore.currentApp && confirm(`"${appStore.currentApp.name}" kitaplığını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) {
    try {
      await appStore.deleteApp(appStore.currentApp.id)
      showHeaderDropdown.value = false
      router.push('/dashboard')
    } catch (error) {
      console.error('Failed to delete app:', error)
    }
  }
}

const toggleFolderDropdown = (folderId: string): void => {
  activeFolderDropdown.value = activeFolderDropdown.value === folderId ? null : folderId
}

const navigateToFolder = (folderId: string | null): void => {
  fileStore.setCurrentFolder(folderId)
  activeFolderDropdown.value = null
}


const handleUploadError = (error: string): void => {
  uploadError.value = error
}

const handleCreateFolder = async (): Promise<void> => {
  try {
    await fileStore.createFolder(appId, { 
      name: newFolderName.value.trim(),
      parentId: fileStore.currentFolderId || undefined
    })
    cancelCreateFolder()
  } catch (error) {
    console.error('Failed to create folder:', error)
  }
}

const cancelCreateFolder = (): void => {
  showCreateFolder.value = false
  newFolderName.value = ''
}

const editFolder = (folder: Folder): void => {
  itemToRename.value = { ...folder, type: 'folder' }
  renameValue.value = folder.name
  activeFolderDropdown.value = null
}

const editFile = (file: FileItem): void => {
  itemToRename.value = { ...file, type: 'file' }
  renameValue.value = file.name
}

const handleRename = async (): Promise<void> => {
  try {
    if (!itemToRename.value) return
    
    if (itemToRename.value.type === 'folder') {
      await fileStore.renameFolder(appId, itemToRename.value.id, renameValue.value.trim())
    } else {
      await fileStore.renameFile(appId, itemToRename.value.id, renameValue.value.trim())
    }
    cancelRename()
  } catch (error) {
    console.error('Failed to rename:', error)
  }
}

const cancelRename = (): void => {
  itemToRename.value = null
  renameValue.value = ''
}

const deleteFolder = async (folder: Folder): Promise<void> => {
  if (confirm(`"${folder.name}" klasörünü silmek istediğinizden emin misiniz?`)) {
    try {
      await fileStore.deleteFolder(appId, folder.id)
      activeFolderDropdown.value = null
    } catch (error) {
      console.error('Failed to delete folder:', error)
    }
  }
}

const deleteFile = async (file: FileItem): Promise<void> => {
  if (confirm(`"${file.name}" dosyasını silmek istediğinizden emin misiniz?`)) {
    try {
      await fileStore.deleteFile(appId, file.id)
    } catch (error) {
      console.error('Failed to delete file:', error)
    }
  }
}

const isUploadingFile = (file: FileItem | UploadingFile): file is UploadingFile => {
  return 'uploadId' in file
}

const getFileIcon = (file: FileItem | UploadingFile): string => {
  const mimeType = file.mimeType || ''
  const fileName = file.name || ''
  
  if (mimeType.includes('image') || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName)) {
    return 'fas fa-image text-emerald-500'
  }
  if (mimeType.includes('video') || /\.(mp4|avi|mov|wmv|flv|webm)$/i.test(fileName)) {
    return 'fas fa-video text-rose-500'
  }
  if (mimeType.includes('audio') || /\.(mp3|wav|flac|aac|ogg)$/i.test(fileName)) {
    return 'fas fa-music text-purple-500'
  }
  if (mimeType.includes('pdf') || /\.pdf$/i.test(fileName)) {
    return 'fas fa-file-pdf text-red-600'
  }
  if (mimeType.includes('document') || mimeType.includes('text') || /\.(doc|docx|txt|rtf)$/i.test(fileName)) {
    return 'fas fa-file-alt text-blue-500'
  }
  if (mimeType.includes('spreadsheet') || /\.(xls|xlsx|csv)$/i.test(fileName)) {
    return 'fas fa-file-excel text-green-600'
  }
  if (mimeType.includes('presentation') || /\.(ppt|pptx)$/i.test(fileName)) {
    return 'fas fa-file-powerpoint text-orange-500'
  }
  if (mimeType.includes('zip') || mimeType.includes('archive') || /\.(zip|rar|7z|tar|gz)$/i.test(fileName)) {
    return 'fas fa-file-archive text-gray-600'
  }
  
  return 'fas fa-file text-slate-500'
}

const getFileDisplayInfo = (file: FileItem | UploadingFile) => {
  if (isUploadingFile(file)) {
    return {
      size: fileStore.formatFileSize(file.size),
      date: 'Yükleniyor...',
      isUploading: true,
      progress: file.progress,
      status: file.status,
      error: file.error
    }
  } else {
    return {
      size: fileStore.formatFileSize(file.size),
      date: formatDate(file.createdAt),
      isUploading: false,
      progress: 0,
      status: 'completed',
      error: undefined
    }
  }
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// Close dropdown when clicking outside
const handleClickOutside = (event: Event): void => {
  const target = event.target as Element
  if (showHeaderDropdown.value && !target?.closest('.relative')) {
    showHeaderDropdown.value = false
  }
  if (activeFolderDropdown.value && !target?.closest('.relative')) {
    activeFolderDropdown.value = null
  }
}

const chunkedUploader = ref<any>(null)

// Check if there are any active uploads
const hasActiveUploads = computed(() => {
  return Object.keys(fileStore.uploadProgress).length > 0
})

onMounted(async () => {
  // Fetch app details and files - using correct store methods
  try {
    await appStore.fetchApps() // Load all apps to get current app
    await fileStore.fetchAppStructure(appId) // Load app structure
    
    // Set current app in store
    const currentApp = appStore.apps.find(app => app.id === appId)
    if (currentApp) {
      appStore.currentApp = currentApp
    }
    
    document.addEventListener('click', handleClickOutside)
  } catch (error) {
    console.error('Failed to load app data:', error)
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script> 